<template>
  <div id="app">
    <!-- v-if, not v-show: the gradient is a WebGL canvas driving its own
         requestAnimationFrame loop, and that loop has no visibility guard —
         `display: none` hid it while it carried on rendering every frame on
         every route that opted out. Unmounting is what actually stops it. -->
    <PageGradient v-if="!hideGradient" class="gradient-canvas" :colors="['#79eac1', '#79eac1', '#33ceff', '#e550d3', '#5865F2']" />
    <div id="app-web" v-if="!showBlankApp">
      <b-modal class="purchase-modal-wrapper" :active="$store.state.purchase.modalOpen" @close="$store.commit('togglePurchaseModal')">
        <PurchaseModal :class="{ 'is-dark-mode': $store.state.purchase.modalItems === 'gold' }"  />
      </b-modal>
      <div class="navbar-wrapper">
        <!-- A transparent navbar is only ever legible because the gradient is
             behind it, so the two are the same decision: no hero, no
             transparency, whatever the scroll position. This used to be a
             separate `darkNav` meta flag, which was set on exactly the routes
             that already opted out of the gradient and drifted the moment one
             opted out without it. -->
        <div class="navbar-background" :class="{ pinned: scrollPosition > 1 || hideGradient }">
          <NavBar @toggleSidebar="toggleSidebar" />
        </div>
      </div>
      <div class="router-wrapper" :class="{ pinned: isPinned }">
        <router-view class="router-view"/>
      </div>
      <NavSidebar :active-route="activeRoute" :open="navOpen" @close="closeNav"/>
      <Footer v-if="pageLoaded" />
    </div>

    <div id="app-blank" v-else>
      <router-view />
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/Nav/NavBar.vue'
import NavSidebar from '@/components/Nav/NavSidebar.vue'
import Footer from '@/components/Page/PageFooter.vue'
import PurchaseModal from '@/components/Purchase/PurchaseModal.vue'

import PageGradient from '@/components/Page/PageGradient.vue'

export default {
  components: {
    NavBar,
    NavSidebar,
    Footer,
    PurchaseModal,
    PageGradient
  },
  data() {
    return {
      navOpen: false,
      scrollPosition: 0,      
      pageLoaded: false
    }
  },
  computed: {
    activeRoute() {
      return this.$route.path
    },
    /**
     * Route meta, inherited from parent routes.
     *
     * `$route.meta` is only the *matched leaf's* meta, so a flag set on a
     * parent is invisible to its children. /profile/:id declares
     * `hideGradient` and renders its panels as child routes — read the leaf
     * alone and every one of them silently puts the WebGL gradient back
     * behind a page that turned it off for the frame budget.
     */
    routeMeta() {
      return this.$route.matched.reduce((meta, record) => ({ ...meta, ...record.meta }), {})
    },
    showBlankApp() {
      return !!this.routeMeta.blank
    },
    hideGradient() {
      return !!this.routeMeta.hideGradient || this.showBlankApp
    },
    isPinned() {
      return this.scrollPosition > 1 || this.activeRoute.startsWith('/dashboard')
    },
  },
  methods: {
    toggleSidebar() {
      this.navOpen = !this.navOpen
    },
    closeNav() {
      this.navOpen = false
    },
    updateScroll() {
      this.scrollPosition = window.scrollY
    },
    async initialize() {
      // Has to come after the user id is known (the request is keyed off it),
      // but fetchAllUserInfo() is guaranteed not to reject, so this is always
      // reached — it used to sit behind an await that could throw, which is
      // how the crown went missing on every page except /premium.
      //
      // Not awaited itself: the nav bar's crown is the only thing that needs
      // it outside /profile/me (which fetches its own, fresher copy on
      // mount), and it shouldn't hold up first paint.
      await this.$store.dispatch('fetchAllUserInfo')
      this.$store.dispatch('profile/fetchPremiumStatus')
      await this.$store.dispatch('checkout/init')
      this.handleStripeRedirectReturn()
    },
    // Most card payments confirm inline without ever leaving the page, but
    // some (3D Secure, certain banks) redirect out to authenticate and back
    // to `return_url` — this picks that back up wherever it lands.
    async handleStripeRedirectReturn() {
      const params = new URLSearchParams(window.location.search)
      const clientSecret = params.get('payment_intent_client_secret')
      if (!clientSecret) return

      const stripe = await this.$store.dispatch('checkout/init')
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        this.$buefy.toast.open({ message: 'Purchase complete! Crediting your account…', type: 'is-success', duration: 5000 })
        this.$store.dispatch('fetchDBInfo')
      } else if (paymentIntent && paymentIntent.status !== 'canceled') {
        this.$buefy.toast.open({ message: 'Your payment is still processing — check back in a moment.', type: 'is-warning', duration: 5000 })
      }

      params.delete('payment_intent')
      params.delete('payment_intent_client_secret')
      params.delete('redirect_status')
      const query = params.toString()
      history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''))
    },
  },
  beforeMount() {
    // Fire-and-forget, so it needs its own catch — without one a rejection in
    // here is an unhandled promise nobody sees, which is precisely how the
    // bug above stayed invisible.
    this.initialize().catch(err => console.error('App initialization failed:', err))
    this.$router.onReady(function() {
      this.pageLoaded = true
    }.bind(this))
  },
  mounted() {
    window.addEventListener('scroll', this.updateScroll);
  },
  destroy() {
    window.removeEventListener('scroll', this.updateScroll)
  }

}
</script>

<style lang="scss">
// Global styles
@import "@/assets/styles/buefy.scss";

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  .is-hidden-mobile, .is-0-mobile {
      @include mobile {
          display: none;
      }
  }
}

.router-wrapper {
  margin-top: 0;
  &.pinned {
    margin-top: -3.5rem;
  }
}

.navbar-wrapper {
  margin-bottom: -3.5rem;
  max-height: 4rem;
  z-index: 30;
  .navbar-background {
    z-index: 30;
    background-color: unset;
    transition: all 0.2s;
    position: fixed;
    top: 0; left: 0;
    width: 100vw;
    .navbar-end {
      padding-right: 20px;
    }
    &.pinned {
      background-color: rgba(78, 77, 83, 0.7);
      backdrop-filter: blur(10px);
      color: black;
    }
  }
}

</style>
